import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, ClockIcon, ExamIcon, QuestionIcon } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import PurchasePackModal from '../../components/modals/PurchasePackModal';
import { DarkModeToggle } from '../../Layout/DarkModeToggle';

export default function UnlockedTestDetails() {
  const navigate = useNavigate();
  const { packId, testId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(true);
  const [testDetails, setTestDetails] = useState(null);
  const [testTitle, setTestTitle] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [totalQs, setTotalQs] = useState(75);
  const [testDuration, setTestDuration] = useState(180);
  const [testMarks, setTestMarks] = useState(300);
  const [isPackPurchased, setIsPackPurchased] = useState(true);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  const user = JSON.parse(localStorage.getItem('userProfile'));
  const selectedPack = JSON.parse(localStorage.getItem('selectedPack'));

  const testStats = [
    {
      icon: <QuestionIcon className="h-5 w-5 text-orange-500" />,
      value: '8 Oct, 9:30 PM',
      title: 'TOTAL QS',
    },
    {
      icon: <ClockIcon className="h-5 w-5 text-blue-500" />,
      value: '180 min',
      title: 'TEST DURATION',
    },
    {
      icon: <ExamIcon className="h-5 w-5 text-blue-500" />,
      value: '3',
      title: 'TOTAL SCORE',
    },
  ];

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const res = await axios.get(`/api/test/${packId}/${testId}`);
        const data = res?.data;
        if (data?.userType === 'free' && data?.type === 'paid') {
          setIsPackPurchased(false);
        }
        const now = new Date();
        const startDate = new Date(data?.startDate);
        if (now < startDate) {
          navigate(`/v3/${packId}/tests`);
        }
        if (data?.isAttempted) {
          navigate(`/${packId}/${testId}/analysis`);
        }
        if (data?.isExpired || data?.packExpired) {
          navigate('/v2/home');
        }
        setTestDetails(data);
        setTestTitle(data?.title);
        const startDateObj = new Date(data?.startDate);
        const formattedDate = startDateObj.toLocaleString('en-GB', {
          day: 'numeric',
          month: 'short',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
        setReleaseDate(formattedDate);
        setTotalQs(data?.totalQuestions);
        setTestMarks(data?.totalMarks);
        setTestDuration(data?.totalTime);
        window.webengage.track('Viewed Test Details', {
          'User Id': user?.email,
          'Test Id': testId,
          'Pack Id': packId,
          'Test Name': data?.title,
          'Pack Name': data?.packTitle,
          Source: searchParams?.get('source') || 'direct',
        });
      } catch (error) {
        navigate('/v2/home');
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [packId, testId]);

  const openTestInNewWindow = () => {
    const source = searchParams?.get('source') || 'direct';
    const token = cookies?.token;

    const url = `https://test-staging.quizrr.in/tests/${packId}/${testId}/instructions?source=${source}&t=${token}`;

    // Window features
    const params = `
      top=0,left=0,
      width=${window.screen.width},height=${window.screen.height},
      statusbar=no,toolbar=no,location=no,directories=no,menubar=no,resizable=no,
      scrollbars=yes,status=no,fullscreen=yes
    `;

    const newWin = window.open(url, '_blank', params);

    if (newWin && newWin.focus) {
      newWin.focus();
    }
  };

  return (
    <div className="h-screen overflow-auto flex pb-14 lg:pb-0 bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)]">
      <div className="invisible">
        <DarkModeToggle />
      </div>
      <div className="w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex flex-col w-full items-start gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="bg-[var(--q3-surface-dim)] rounded-full"
              onClick={() => navigate(`/v3/${packId}/tests`)}
            >
              <ArrowLeftIcon size={24} />
            </Button>
            <div className="flex flex-col w-full gap-4">
              <div className="flex flex-row justify-between items-center w-full">
                <div className="flex-grow overflow-hidden">
                  <p className="text-2xl font-bold truncate text-wrap">{testTitle}</p>
                </div>
                <Button
                  className="lg:block hidden bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-dark)] text-[var(--q3-neutral-default_inverted)] px-6 py-2 rounded-full ml-4"
                  onClick={() => {
                    if (isPackPurchased) {
                      window.webengage.track('Test Started', {
                        'User id': user?.email,
                        'Pack Id': packId,
                        'Pack Name': selectedPack?.title,
                        'Test Id': testId,
                        'Test Name': testTitle,
                        Source: searchParams.get('source') || 'direct',
                      });
                      navigate(
                        `/tests/${packId}/${testId}/instructions?source=${searchParams?.get('source') || 'direct'}`,
                      );
                    } else {
                      setShowPurchaseModal(true);
                    }
                  }}
                >
                  Attempt Test
                </Button>
              </div>
              <p className="text-[var(--q3-neutral-light)] text-sm">
                Release Date: <span className="font-bold">{releaseDate}</span>
              </p>
              <Button
                className="lg:hidden block bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-dark)] text-[var(--q3-neutral-default_inverted)] rounded-full"
                onClick={() => {
                  if (isPackPurchased) {
                    window.webengage.track('Test Started', {
                      'User id': user?.email,
                      'Pack Id': packId,
                      'Pack Name': selectedPack?.title,
                      'Test Id': testId,
                      'Test Name': testTitle,
                      Source: searchParams.get('source') || 'direct',
                    });
                    navigate(
                      `/tests/${packId}/${testId}/instructions?source=${searchParams?.get('source') || 'direct'}`,
                    );
                  } else {
                    setShowPurchaseModal(true);
                  }
                }}
              >
                Attempt Test
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {testStats.map((stat, index) => (
            <div
              key={index}
              className="flex flex-col p-4 pt-3 h-[116px] pr-3 border border-[var(--q3-stroke-normal)] rounded-lg"
            >
              <div className={`rounded-lg flex flex-row gap-2 items-center`}>
                {stat.icon} <p className="text-xs">{stat.title}</p>
              </div>
              <div className="text-4xl font-semibold mt-auto">
                {stat.title === 'TOTAL QS' ? (
                  <>
                    {totalQs}
                    <span className="text-sm font-normal ml-1">Qs</span>
                  </>
                ) : stat.title === 'TEST DURATION' ? (
                  <>
                    {testDuration}
                    <span className="text-sm font-normal ml-1">min</span>
                  </>
                ) : (
                  testMarks
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-col mt-6 gap-6 w-full bg-[var(--q3-surface-default)]">
          <Accordion type="single" collapsible defaultValue="syllabus" className="w-full">
            <AccordionItem value="syllabus" className="border border-[var(--q3-stroke-normal)] rounded-lg px-4">
              <AccordionTrigger className="text-lg font-bold py-4">Syllabus</AccordionTrigger>
              <AccordionContent className="pb-4">
                <div
                  className="[&_h2]:text-lg [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-6"
                  dangerouslySetInnerHTML={{ __html: testDetails?.syllabus }}
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
          <div className="w-full bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-lg p-4 mb-4">
            <div className="pb-4">
              <p className="font-bold text-lg">Exam Structure & Marking Scheme</p>{' '}
              <p className="text-[var(--q3-neutral-light)]">
                This paper is divided into {testDetails?.sections?.length} sections
              </p>
            </div>
            <div className="space-y-4">
              {testDetails?.subjects?.map((subject) => (
                <div key={subject?.subjectId?._id}>
                  <div className="flex flex-row gap-2 items-center">
                    <img src={subject?.subjectId?.icon} className="w-5 h-5" alt={subject?.subjectId?.title} />
                    <p className="text-sm font-medium">{subject?.subjectId?.title}</p>
                  </div>
                  <ul className="list-outside space-y-1 pt-1">
                    {testDetails?.sections
                      ?.filter(
                        (section) => section?.sectionId?.subject?.toString() === subject?.subjectId?._id.toString(),
                      )
                      .map((section) => {
                        const rawTitle = section?.sectionId?.title || '';
                        const subjectTitle = subject?.subjectId?.title || '';
                        const cleanedTitle = rawTitle.replace(subjectTitle, '').trim();
                        const totalQuestions = section?.sectionId?.questions?.length;
                        const attemptableQuestions = section?.sectionId?.maximumAttemptLimit;
                        const markingScheme = section?.sectionId?.markingScheme;
                        const isMCQSection = testDetails?.exam?.[0]?.title === 'JEE Advanced' && cleanedTitle === 'MCQ';

                        return (
                          <li
                            key={section?.sectionId?._id}
                            className="pl-2 text-sm text-[var(--q3-neutral-light)] list-disc ml-6"
                          >
                            {cleanedTitle}:{' '}
                            {`${totalQuestions} questions${totalQuestions !== attemptableQuestions ? `, ${attemptableQuestions} attemptable` : ''} (+${markingScheme?.correctAttempt} for correct, ${isMCQSection && '+3 If all four options are correct but ONLY three correct options are chosen, +2 If three or more options are correct but only two correct options are chosen, +1 If two or more options are correct but only one correct option is chosen'} ${markingScheme.incorrectAttempt} for incorrect, 0 for no response)`}
                          </li>
                        );
                      })}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {showPurchaseModal && (
        <PurchasePackModal
          packTitle={testDetails?.packTitle}
          paymentItemId={testDetails?.paymentItemId}
          couponCode={testDetails?.couponCode}
          exam={testDetails?.exam}
          onClose={() => setShowPurchaseModal(false)}
        />
      )}
    </div>
  );
}
